import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
/* eslint-disable react-hooks/exhaustive-deps */

/**
 * FlickeringGrid — a dependency-free canvas grid of squares that randomly
 * flicker in and out. A self-contained port of the magicui/aceternity effect
 * (no framer-motion, no Tailwind), sized to fill its positioned parent.
 *
 * Rendering is devicePixelRatio-aware and pauses when the element is scrolled
 * out of view or the tab is hidden. Honors `prefers-reduced-motion` by holding a
 * static frame. The canvas is non-interactive (pointer-events: none).
 */
export default function FlickeringGrid({
  squareSize = 4,
  gridGap = 6,
  flickerChance = 0.3,
  color = "rgb(120, 170, 255)",
  maxOpacity = 0.35,
  className,
  style,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const inViewRef = useRef(true);

  // Convert the requested color to an "r,g,b" prefix once (canvas fillStyle).
  const toRgbPrefix = useCallback((c) => {
    if (typeof document === "undefined") return "0, 88, 190";
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "0, 88, 190";
    ctx.fillStyle = c;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return `${r}, ${g}, ${b}`;
  }, []);

  const setupGrid = useCallback(
    (canvas, ctx, width, height, dpr) => {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const cols = Math.floor(width / (squareSize + gridGap));
      const rows = Math.floor(height / (squareSize + gridGap));
      const squares = new Float32Array(cols * rows);
      for (let i = 0; i < squares.length; i++) {
        squares[i] = Math.random() * maxOpacity;
      }
      return { cols, rows, squares, dpr };
    },
    [squareSize, gridGap, maxOpacity]
  );

  const updateSquares = useCallback(
    (squares, deltaTime) => {
      for (let i = 0; i < squares.length; i++) {
        if (Math.random() < flickerChance * deltaTime) {
          squares[i] = Math.random() * maxOpacity;
        }
      }
    },
    [flickerChance, maxOpacity]
  );

  const drawGrid = useCallback(
    (ctx, width, height, cols, rows, squares, dpr, rgb) => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const opacity = squares[i * rows + j];
          ctx.fillStyle = `rgba(${rgb}, ${opacity})`;
          ctx.fillRect(
            i * (squareSize + gridGap) * dpr,
            j * (squareSize + gridGap) * dpr,
            squareSize * dpr,
            squareSize * dpr
          );
        }
      }
    },
    [squareSize, gridGap]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const rgb = toRgbPrefix(color);
    const dpr = window.devicePixelRatio || 1;
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let rafId;
    let lastTime = 0;
    let grid = setupGrid(
      canvas,
      ctx,
      container.clientWidth,
      container.clientHeight,
      dpr
    );

    const animate = (time) => {
      if (!inViewRef.current) {
        rafId = 0;
        return;
      }
      const deltaTime = lastTime ? (time - lastTime) / 1000 : 0;
      lastTime = time;
      if (!reduceMotion) updateSquares(grid.squares, deltaTime);
      drawGrid(
        ctx,
        canvas.width,
        canvas.height,
        grid.cols,
        grid.rows,
        grid.squares,
        grid.dpr,
        rgb
      );
      rafId = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(() => {
      grid = setupGrid(
        canvas,
        ctx,
        container.clientWidth,
        container.clientHeight,
        dpr
      );
    });
    resizeObserver.observe(container);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        // Resume the loop when the panel scrolls back into view.
        if (entry.isIntersecting && !rafId) {
          lastTime = 0;
          rafId = requestAnimationFrame(animate);
        }
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(canvas);

    rafId = requestAnimationFrame(animate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [color, setupGrid, updateSquares, drawGrid, toRgbPrefix]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        ...style,
      }}
    >
      <canvas ref={canvasRef} style={{ display: "block" }} />
    </div>
  );
}

FlickeringGrid.propTypes = {
  squareSize: PropTypes.number,
  gridGap: PropTypes.number,
  flickerChance: PropTypes.number,
  color: PropTypes.string,
  maxOpacity: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
};
